import {
  Button,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Switch,
  Upload,
  message,
  notification,
} from 'antd'
import './AddSupplierModal.style.less'
import { useMutation } from 'react-query'
import {
  saveSupplier,
  updateSupplier,
  uploadSupplierDocuments,
} from '../../../DataManagement.transform'
import { queryClient } from '@/config/react-query'
import { useEffect } from 'react'
import { JSONStringify, isEmptyValue, parseJSON } from '@/utils/common'
import LoadingComponent from '@/components/LoadingComponent/LoadingComponent'
import { UploadOutlined } from '@ant-design/icons'

interface Props {
  visible: boolean
  onClose: () => void
  data: any
  country: any[]
}

function AddSupplierModal(props: Props) {
  const [form] = Form.useForm()

  const onSaveSupplier = async (data) => {
    const payload = {
      name: data.name,
      is_factory: data?.isFactory,
      type: 'supplier',
      address: data?.address,
      country_id: parseJSON(data?.country).id,
      contact_person_name: data?.contactName,
      phone_number: data?.phoneNumber,
      email: data?.email,
      code: data?.code,
      rating: data?.rating,
      remarks: data?.remarks,
      compliance_check: data?.compliance_check,
      property_value: {
        values: [
          {
            name: 'Coordinates',
            field: 'coordinates',
            data_type: 'text',
            value: data?.coordinates,
            required: false,
          },
          {
            name: 'Location URL',
            field: 'location_url',
            data_type: 'text',
            value: data?.locationUrl,
            required: false,
          },
          {
            name: 'Documents',
            field: 'documents',
            data_type: 'file',
            value: await uploadSupplierDocuments(data?.documents),
            required: false,
          },
        ],
      },
    }

    if (!isEmptyValue(props.data)) {
      const res = await updateMutation.mutateAsync(payload)
      if (!isEmptyValue(res)) {
        notification.error({ message: res, duration: 3, placement: 'top' })
        return
      }
    } else {
      const res = await mutation.mutateAsync(payload)
      if (!isEmptyValue(res)) {
        notification.error({ message: res, duration: 3, placement: 'top' })
        return
      }
    }

    await queryClient.refetchQueries(['Supplier'])
    props.onClose()
  }

  const mutation = useMutation(async (payload: any) => {
    return await saveSupplier(payload)
  })

  const updateMutation = useMutation(async (payload: any) => {
    return await updateSupplier(props.data.id, payload)
  })

  useEffect(() => {
    if (!isEmptyValue(props.data)) {
      const { data } = props
      form.setFieldsValue({
        name: data?.name,
        isFactory: data?.is_factory,
        address: data?.address,
        country: JSONStringify(data?.country),
        contactName: data?.contact_person_name,
        phoneNumber: data?.phone_number,
        code: data?.code,
        email: data?.email,
        rating: data?.rating,
        remarks: data?.remarks,
        compliance_check: data?.compliance_check,
        coordinates: data?.property_value?.values?.find(
          (item) => item?.field == 'coordinates'
        )?.value,
        locationUrl: data?.property_value?.values?.find(
          (item) => item?.field == 'location_url'
        )?.value,
        documents: data?.property_value?.values?.find(
          (item) => item?.field == 'documents'
        )?.value,
      })
    }
  }, [props.data])

  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e
    }
    return e?.fileList?.map((item) => {
      return { ...item, status: 'done' }
    })
  }

  const validateFileSize = (value) => {
    if (value.size > 5242880) {
      message.error('Each File cannot be over 5mbps', 3)
      return Upload.LIST_IGNORE
    }
  }

  return (
    <Modal
      className="modal-add-supplier-container"
      open={props.visible}
      width={660}
      footer={false}
      maskClosable={false}
      onCancel={props.onClose}
    >
      <div>
        {mutation.isLoading && <LoadingComponent />}
        {updateMutation.isLoading && <LoadingComponent />}
        <p className="title">
          {' '}
          {!isEmptyValue(props.data) ? 'Edit' : 'New'} Supplier
        </p>
        <Form labelCol={{ span: 7 }} form={form} onFinish={onSaveSupplier}>
          <Form.Item
            label="Supplier Name"
            name="name"
            rules={[{ required: true, message: 'This field is required' }]}
          >
            <Input size="large" placeholder="Text" />
          </Form.Item>
          <Form.Item
            initialValue={props?.data?.is_factory}
            name="isFactory"
            label="Supplier is Factory"
          >
            <Switch
              defaultChecked={props?.data?.is_factory}
              checkedChildren="Yes"
              unCheckedChildren="No"
            />
          </Form.Item>{' '}
          <Form.Item label="Code" name="code">
            <Input size="large" placeholder="Text" />
          </Form.Item>
          <Form.Item name="address" label="Address">
            <Input size="large" placeholder="Text" />
          </Form.Item>
          <Form.Item
            name="country"
            rules={[{ required: true, message: 'This field is required' }]}
            label="Country"
          >
            <Select
              size={'large'}
              placeholder="Select"
              options={props.country}
              allowClear
              showSearch
            />
          </Form.Item>
          <Form.Item name="locationUrl" label="Google Map">
            <Input size="large" placeholder="IFrame" />
          </Form.Item>
          <Form.Item name="coordinates" label="Coordinates">
            <Input size="large" placeholder="Text" />
          </Form.Item>
          <Form.Item name="contactName" label="Contact Name">
            <Input size="large" placeholder="Text" />
          </Form.Item>
          <Form.Item name="phoneNumber" label="Phone Number">
            <Input size="large" placeholder="Phone" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[{ required: true, message: 'This field is required' }]}
          >
            <Input size="large" placeholder="Text" />
          </Form.Item>
          <Form.Item name="compliance_check" label="Compliance Check">
            <Input size="large" placeholder="Text" />
          </Form.Item>
          <Form.Item name="rating" label="Rating">
            <Input size="large" placeholder="Text" />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks / CAP">
            <Input size="large" placeholder="Text" />
          </Form.Item>
          <Form.Item
            name="documents"
            label="Documents"
            valuePropName="fileList"
            getValueFromEvent={normFile}
          >
            <Upload
              maxCount={10}
              multiple
              accept=".doc,.docx,.docm,.xls,.xlsx,xlsm,.csv,.ppt,.pptx,.ppts,.txt,.rtf,.md,.jpg,.jpeg,.png,.bmp,.tif,.tiff,.svg,.eps,.raw,.psd,.ai,.pdf,.ico,.webp,.gif"
              beforeUpload={validateFileSize}
            >
              <Button
                className="upload-button"
                size="large"
                icon={<UploadOutlined />}
              >
                Upload File
              </Button>
            </Upload>
          </Form.Item>
          <Row className="button-container">
            <Button onClick={props.onClose} size="large">
              Cancel
            </Button>
            <Button
              size="large"
              type="primary"
              htmlType="submit"
              loading={mutation.isLoading || updateMutation.isLoading}
            >
              {!isEmptyValue(props.data) ? 'Save' : 'Create'}
            </Button>
          </Row>
        </Form>
      </div>
    </Modal>
  )
}

export default AddSupplierModal
